import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { loadStripe } from '@stripe/stripe-js';
import toast from 'react-hot-toast';
import Navbar from '../../components/public/Navbar';
import Footer from '../../components/public/Footer';
import GemCard from '../../components/public/GemCard';
import { useCart } from '../../context/CartContext';
import api from '../../utils/api';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || 'pk_test_placeholder');

// ─────────────────────────────────────────────────────────────────
// GEM DETAIL PAGE
// ─────────────────────────────────────────────────────────────────
export function GemDetailPage() {
  const { slug } = useParams();
  const { addToCart } = useCart();
  const [gem, setGem]       = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [enquiry, setEnquiry] = useState({ name:'', email:'', phone:'', country:'', message:'' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/gems/${slug}`).then(r => {
      setGem(r.data.data);
      setRelated(r.data.related || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [slug]);

  const handleAdd = () => { addToCart(gem); toast.success(`${gem.name} added to cart`); };

  const handleEnquiry = async (e) => {
    e.preventDefault(); setSending(true);
    try {
      await api.post('/enquiries', { ...enquiry, gem_id: gem.id, gem_name: gem.name });
      toast.success('Enquiry sent! We\'ll respond within 24 hours.');
      setShowEnquiry(false);
      setEnquiry({ name:'', email:'', phone:'', country:'', message:'' });
    } catch { toast.error('Failed to send enquiry. Please try again.'); }
    finally { setSending(false); }
  };

  const imgs = gem?.images ? (typeof gem.images === 'string' ? JSON.parse(gem.images) : gem.images).filter(Boolean) : [];

  if (loading) return (
    <>
      <Navbar />
      <div style={{ paddingTop:120, minHeight:'80vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div className="spinner" style={{ width:48, height:48 }} />
      </div>
      <Footer />
    </>
  );

  if (!gem) return (
    <>
      <Navbar />
      <div style={{ paddingTop:140, textAlign:'center', minHeight:'60vh', padding:'140px 20px 60px' }}>
        <h2 style={{ fontFamily:'var(--font-serif)', fontSize:32, color:'var(--muted)', marginBottom:24 }}>Gem not found</h2>
        <Link to="/shop" className="btn btn-gold">Browse Collection</Link>
      </div>
      <Footer />
    </>
  );

  const mainImg = imgs[activeImg]?.url || gem.primary_image || 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=900&q=80';

  return (
    <>
      <Helmet>
        <title>{gem.name} — Natural Ceylon {gem.category_name} | Sudath Gems</title>
        <meta name="description" content={gem.short_desc || gem.description?.slice(0,155)} />
      </Helmet>
      <Navbar />
      <div style={{ paddingTop: 72 }}>
        {/* Breadcrumb */}
        <div style={{ background:'var(--surface)', borderBottom:'1px solid var(--border)', padding:'16px 0' }}>
          <div className="container">
            <div style={{ display:'flex', gap:8, alignItems:'center', fontSize:12, color:'var(--muted)' }}>
              <Link to="/" style={{ color:'var(--muted)' }}>Home</Link>
              <span>›</span>
              <Link to="/shop" style={{ color:'var(--muted)' }}>Collection</Link>
              <span>›</span>
              <Link to={`/shop?category=${gem.category_slug}`} style={{ color:'var(--muted)' }}>{gem.category_name}</Link>
              <span>›</span>
              <span style={{ color:'var(--white)' }}>{gem.name}</span>
            </div>
          </div>
        </div>

        <div className="container" style={{ padding:'64px 40px' }}>
          <div className="gem-detail-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:80, alignItems:'start' }}>

            {/* Images */}
            <div>
              <div style={{ position:'relative', paddingBottom:'100%', overflow:'hidden', borderRadius:8, background:'var(--surface2)', marginBottom:12 }}>
                <img src={mainImg} alt={gem.name}
                  style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', transition:'all 0.4s ease' }} />
                {gem.tag && <span className="badge badge-gold" style={{ position:'absolute', top:20, left:20 }}>{gem.tag}</span>}
              </div>
              {imgs.length > 1 && (
                <div style={{ display:'flex', gap:8 }}>
                  {imgs.map((img, i) => (
                    <div key={i} onClick={() => setActiveImg(i)} style={{
                      width:72, height:72, borderRadius:4, overflow:'hidden',
                      border:`2px solid ${i===activeImg ? 'var(--gold)' : 'var(--border)'}`,
                      cursor:'pointer', transition:'var(--transition)',
                    }}>
                      <img src={img.url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div>
              <p style={{ fontSize:11, letterSpacing:3, textTransform:'uppercase', color:'var(--gold)', marginBottom:12 }}>
                {gem.category_name} · Sri Lanka
              </p>
              <h1 style={{ fontFamily:'var(--font-serif)', fontSize:'clamp(1.8rem,4vw,2.8rem)', fontWeight:300, color:'var(--white)', marginBottom:16, lineHeight:1.2 }}>
                {gem.name}
              </h1>
              <div style={{ width:50, height:1, background:'var(--gold)', marginBottom:24 }} />

              <div style={{ display:'flex', alignItems:'baseline', gap:12, marginBottom:28 }}>
                <span style={{ fontFamily:'var(--font-serif)', fontSize:40, color:'var(--gold)', fontWeight:300 }}>
                  ${Number(gem.price_usd).toLocaleString('en-US')}
                </span>
                <span style={{ fontSize:14, color:'var(--muted)' }}>USD</span>
                {gem.compare_price && <span style={{ fontSize:16, color:'var(--muted2)', textDecoration:'line-through' }}>${Number(gem.compare_price).toLocaleString()}</span>}
              </div>

              {/* Specs grid */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:1, border:'1px solid var(--border)', borderRadius:4, overflow:'hidden', marginBottom:32 }}>
                {[
                  ['Weight', gem.weight_ct ? `${gem.weight_ct} ct` : '—'],
                  ['Color', gem.color || '—'],
                  ['Clarity', gem.clarity || '—'],
                  ['Cut', gem.cut || '—'],
                  ['Treatment', gem.treatment || '—'],
                  ['Origin', gem.origin || 'Sri Lanka'],
                  ['Mine', gem.mine_location || '—'],
                  ['Certificate', gem.certification || '—'],
                ].map(([label, val]) => (
                  <div key={label} style={{ padding:'14px 16px', background:'var(--surface2)', display:'flex', flexDirection:'column', gap:4 }}>
                    <span style={{ fontSize:10, letterSpacing:1.5, textTransform:'uppercase', color:'var(--muted)' }}>{label}</span>
                    <span style={{ fontSize:13, color:'var(--white)', fontWeight:500 }}>{val}</span>
                  </div>
                ))}
              </div>

              <p style={{ fontSize:14, color:'var(--muted)', lineHeight:1.9, marginBottom:32 }}>{gem.description}</p>

              {/* Stock */}
              {gem.stock_qty > 0 ? (
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:24 }}>
                  <div style={{ width:8, height:8, borderRadius:'50%', background:'var(--success)' }} />
                  <span style={{ fontSize:12, color:'var(--success)', letterSpacing:1 }}>
                    {gem.stock_qty <= 2 ? `Only ${gem.stock_qty} available` : 'In Stock'}
                  </span>
                </div>
              ) : (
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:24 }}>
                  <div style={{ width:8, height:8, borderRadius:'50%', background:'var(--danger)' }} />
                  <span style={{ fontSize:12, color:'var(--danger)', letterSpacing:1 }}>Sold — Contact for Similar</span>
                </div>
              )}

              <div style={{ display:'flex', flexDirection:'column', gap:12, marginBottom:32 }}>
                {gem.stock_qty > 0 && (
                  <button onClick={handleAdd} className="btn btn-gold btn-lg" style={{ justifyContent:'center' }}>
                    Add to Cart — ${Number(gem.price_usd).toLocaleString()} USD
                  </button>
                )}
                <button onClick={() => setShowEnquiry(true)} className="btn btn-outline" style={{ justifyContent:'center' }}>
                  Enquire About This Gem
                </button>
              </div>

              {/* Trust */}
              <div style={{ display:'flex', gap:20, flexWrap:'wrap', paddingTop:24, borderTop:'1px solid var(--border)' }}>
                {['🏅 GIA Certified', '🚢 Worldwide Shipping', '🔒 Secure Payment', '↩️ 30-Day Returns'].map(t => (
                  <span key={t} style={{ fontSize:11, color:'var(--muted)', letterSpacing:0.5 }}>{t}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Related */}
          {related.length > 0 && (
            <div style={{ marginTop:100 }}>
              <h2 style={{ fontFamily:'var(--font-serif)', fontSize:28, fontWeight:300, color:'var(--white)', marginBottom:8, textAlign:'center' }}>You May Also Love</h2>
              <div className="gold-divider" style={{ marginBottom:48 }} />
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:24 }}>
                {related.map(g => <GemCard key={g.id} gem={g} />)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Enquiry Modal */}
      {showEnquiry && (
        <div className="modal-overlay" onClick={() => setShowEnquiry(false)}>
          <div className="modal-box" onClick={e=>e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
              <h3 style={{ fontFamily:'var(--font-serif)', fontSize:24, fontWeight:400 }}>Enquire About This Gem</h3>
              <button onClick={() => setShowEnquiry(false)} style={{ color:'var(--muted)', fontSize:24 }}>✕</button>
            </div>
            <p style={{ fontSize:13, color:'var(--muted)', marginBottom:24, lineHeight:1.7 }}>
              Our gemologist will respond to your enquiry within 24 hours. We welcome questions about certification, custom orders, and wholesale pricing.
            </p>
            <div style={{ padding:'16px', background:'var(--surface2)', borderRadius:4, marginBottom:24, border:'1px solid var(--border2)' }}>
              <p style={{ fontSize:12, color:'var(--gold)' }}>Regarding: <strong style={{ color:'var(--white)' }}>{gem.name}</strong> — ${Number(gem.price_usd).toLocaleString()} USD</p>
            </div>
            <form onSubmit={handleEnquiry}>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Your Name *</label>
                  <input required value={enquiry.name} onChange={e=>setEnquiry({...enquiry,name:e.target.value})} className="form-input" placeholder="Full name" />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input required type="email" value={enquiry.email} onChange={e=>setEnquiry({...enquiry,email:e.target.value})} className="form-input" placeholder="your@email.com" />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone / WhatsApp</label>
                  <input value={enquiry.phone} onChange={e=>setEnquiry({...enquiry,phone:e.target.value})} className="form-input" placeholder="+1 234 567 8900" />
                </div>
                <div className="form-group">
                  <label className="form-label">Country</label>
                  <input value={enquiry.country} onChange={e=>setEnquiry({...enquiry,country:e.target.value})} className="form-input" placeholder="United States" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Message *</label>
                <textarea required value={enquiry.message} onChange={e=>setEnquiry({...enquiry,message:e.target.value})} className="form-input form-textarea" placeholder="I am interested in this gem. Could you provide more information about the certification and shipping?" />
              </div>
              <div style={{ display:'flex', gap:12, justifyContent:'flex-end' }}>
                <button type="button" onClick={()=>setShowEnquiry(false)} className="btn btn-outline">Cancel</button>
                <button type="submit" disabled={sending} className="btn btn-gold">{sending ? 'Sending...' : 'Send Enquiry'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <Footer />
    </>
  );
}
export default GemDetailPage;
